/**
 * Employer Dashboard (Hiring Hub)
 * Track job postings, candidates, screening services, and subscription
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Building2,
  Briefcase,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  TrendingUp,
  Star,
  Eye,
  Zap,
  ChevronRight,
  Loader2,
  Plus,
  Crown,
  Target,
  BarChart3,
  UserCheck,
  Mail,
  Phone,
  FileText,
  Search,
  Filter,
  MoreVertical,
  MapPin,
  DollarSign,
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Application Status Colors
const STATUS_COLORS = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'New' },
  viewed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Reviewed' },
  shortlisted: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Shortlisted' },
  interview: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Interview' },
  rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
  offer: { bg: 'bg-green-100', text: 'text-green-700', label: 'Offer' },
  hired: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Hired' },
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, change, changeType = 'neutral', color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className={`p-2 rounded-lg ${colors[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          {change && (
            <Badge variant={changeType === 'up' ? 'default' : 'secondary'} className="text-xs">
              {change}
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Job Posting Card Component
const JobPostingCard = ({ job, onView, onManage }) => {
  const isActive = job.status === 'active';
  
  return (
    <Card className={`${!isActive ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-gray-900 truncate">{job.title}</h4>
              <Badge variant={isActive ? 'default' : 'secondary'} className="text-xs">
                {isActive ? 'Active' : 'Closed'}
              </Badge>
              {job.featured && (
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {job.location}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {job.salary_min && job.salary_max 
                  ? `${job.currency_symbol || 'Rs'}${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`
                  : 'Negotiable'
                }
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {job.views || 0} views
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {job.applications_count || 0} applications
            </span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onView}>
              View
            </Button>
            <Button size="sm" onClick={onManage}>
              Manage
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Candidate Card Component
const CandidateCard = ({ application, onUpdateStatus, onView, jobCategory }) => {
  const status = STATUS_COLORS[application.status] || STATUS_COLORS.pending;
  const job = application.job || {};
  const [updating, setUpdating] = useState(false);
  const [badgeSummary, setBadgeSummary] = useState(null);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    await onUpdateStatus(application.id, newStatus);
    setUpdating(false);
  };

  // Fetch badge summary for this candidate
  useEffect(() => {
    const fetchBadges = async () => {
      if (application.candidate_email) {
        try {
          const category = jobCategory || job.category;
          const url = `${API_URL}/api/candidate-badges/employer/candidate-summary/${encodeURIComponent(application.candidate_email)}${category ? `?job_category=${category}` : ''}`;
          const response = await fetch(url);
          const data = await response.json();
          if (data.success) {
            setBadgeSummary(data);
          }
        } catch (error) {
          console.error('Error fetching badges:', error);
        }
      }
    };
    fetchBadges();
  }, [application.candidate_email, jobCategory, job.category]);

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`candidate-card-${application.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">
                {application.candidate_name || `Candidate #${application.id.slice(-6)}`}
              </h4>
              {/* Badge Icons */}
              {badgeSummary?.has_badges && (
                <div className="flex items-center gap-0.5" data-testid="candidate-badge-icons">
                  {badgeSummary.badge_icons.slice(0, 3).map((icon, idx) => (
                    <span 
                      key={idx}
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs ${
                        idx < badgeSummary.category_badges ? 'bg-emerald-100 ring-1 ring-emerald-400' : 'bg-gray-100'
                      }`}
                      title={badgeSummary.badge_names[idx]}
                    >
                      {icon}
                    </span>
                  ))}
                  {badgeSummary.total_badges > 3 && (
                    <span className="text-xs text-gray-400">+{badgeSummary.total_badges - 3}</span>
                  )}
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500">Applied for: {job.title || 'Position'}</p>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Calendar className="h-3 w-3" />
                <span>Applied {new Date(application.created_at).toLocaleDateString()}</span>
              </div>
              {/* Boost indicator */}
              {badgeSummary?.boost_percentage > 0 && (
                <span className="text-xs text-emerald-600 flex items-center gap-0.5" data-testid="candidate-boost">
                  <TrendingUp className="w-3 h-3" />
                  +{badgeSummary.boost_percentage}%
                </span>
              )}
            </div>
          </div>
          <Badge className={`${status.bg} ${status.text}`}>
            {status.label}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <Select 
            value={application.status} 
            onValueChange={handleStatusChange}
            disabled={updating}
          >
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">New</SelectItem>
              <SelectItem value="viewed">Reviewed</SelectItem>
              <SelectItem value="shortlisted">Shortlist</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
              <SelectItem value="offer">Offer</SelectItem>
              <SelectItem value="hired">Hired</SelectItem>
              <SelectItem value="rejected">Reject</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            {application.cv_url && (
              <Button size="sm" variant="outline">
                <FileText className="h-4 w-4 mr-1" />
                CV
              </Button>
            )}
            <Button size="sm" onClick={onView}>
              View Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const EmployerDashboard = () => {
  const navigate = useNavigate();
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationBadges, setApplicationBadges] = useState({}); // Store badge data per application
  const [badgeFilter, setBadgeFilter] = useState(null); // null | 'any' | specific badge id
  const [activeTab, setActiveTab] = useState('overview');
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/job-dashboard/employer/dashboard/${user.id}?country=${country}`
      );
      
      if (response.data.success) {
        setDashboardData(response.data);
        setJobs(response.data.recent_jobs || []);
        setApplications(response.data.recent_applications || []);
        
        // Fetch badges for all applications
        const apps = response.data.recent_applications || [];
        const badgePromises = apps.map(async (app) => {
          if (app.candidate_email) {
            try {
              const badgeRes = await fetch(
                `${API_URL}/api/candidate-badges/employer/candidate-summary/${encodeURIComponent(app.candidate_email)}`
              );
              const badgeData = await badgeRes.json();
              return { appId: app.id, badges: badgeData };
            } catch {
              return { appId: app.id, badges: null };
            }
          }
          return { appId: app.id, badges: null };
        });
        
        const badgeResults = await Promise.all(badgePromises);
        const badgeMap = {};
        badgeResults.forEach(({ appId, badges }) => {
          badgeMap[appId] = badges;
        });
        setApplicationBadges(badgeMap);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter applications by badge
  const getFilteredApplications = () => {
    if (!badgeFilter) return applications;
    
    return applications.filter(app => {
      const badges = applicationBadges[app.id];
      if (!badges || !badges.success) return false;
      
      if (badgeFilter === 'any') {
        return badges.has_badges;
      }
      
      // Filter by specific badge ID
      return badges.badge_ids?.includes(badgeFilter);
    });
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/job-dashboard/employer/application/${applicationId}/status?status=${newStatus}`
      );
      
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
      
      toast({
        title: 'Status updated',
        description: `Application status changed to ${newStatus}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const pipeline = dashboardData?.pipeline || {};
  const subscription = dashboardData?.subscription;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="employer-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-emerald-200 text-sm mb-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/jobs" className="hover:text-white">Jobs</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Hiring Hub</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                <Building2 className="inline h-8 w-8 mr-2" />
                Hiring Hub
              </h1>
              <p className="text-emerald-100">
                Manage your job postings and candidates
              </p>
            </div>
            
            <div className="flex gap-3">
              {subscription ? (
                <Badge className="bg-amber-500 text-white px-4 py-2 text-sm">
                  <Crown className="h-4 w-4 mr-2" />
                  {subscription.plan_name}
                </Badge>
              ) : (
                <Button variant="secondary" onClick={() => navigate('/jobs/employer-subscription')}>
                  Upgrade Plan
                </Button>
              )}
              <Button className="bg-white text-emerald-700 hover:bg-emerald-50" onClick={() => navigate('/jobs/post')}>
                <Plus className="h-4 w-4 mr-2" />
                Post Job
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatsCard
            icon={Briefcase}
            label="Active Jobs"
            value={stats.active_jobs || 0}
            color="blue"
          />
          <StatsCard
            icon={Users}
            label="Total Applicants"
            value={stats.total_applications || 0}
            color="purple"
          />
          <StatsCard
            icon={Clock}
            label="Pending Review"
            value={stats.pending_review || 0}
            color="amber"
          />
          <StatsCard
            icon={Star}
            label="Shortlisted"
            value={stats.shortlisted || 0}
            color="purple"
          />
          <StatsCard
            icon={Calendar}
            label="Interviews"
            value={stats.interviews_scheduled || 0}
            color="blue"
          />
          <StatsCard
            icon={UserCheck}
            label="Screenings"
            value={stats.screenings_used || 0}
            color="green"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="jobs">Job Postings ({jobs.length})</TabsTrigger>
            <TabsTrigger value="candidates">Candidates ({applications.length})</TabsTrigger>
            <TabsTrigger value="screening">Screening Services</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Hiring Pipeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-emerald-600" />
                  Hiring Pipeline
                </CardTitle>
                <CardDescription>Track candidates through your hiring process</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                  {Object.entries(pipeline).map(([stage, count]) => {
                    const status = STATUS_COLORS[stage] || STATUS_COLORS.pending;
                    return (
                      <div key={stage} className="text-center p-3 rounded-lg bg-gray-50">
                        <div className={`inline-flex p-2 rounded-full ${status.bg} mb-2`}>
                          <span className={`text-lg font-bold ${status.text}`}>{count}</span>
                        </div>
                        <p className="text-xs text-gray-500 capitalize">{status.label || stage}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Job Postings */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent Job Postings</CardTitle>
                  <CardDescription>Your latest job listings</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('jobs')}>
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {jobs.slice(0, 3).map((job) => (
                    <JobPostingCard
                      key={job.id}
                      job={job}
                      onView={() => navigate(`/jobs/${job.id}`)}
                      onManage={() => navigate(`/jobs/${job.id}/manage`)}
                    />
                  ))}
                  {jobs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No job postings yet</p>
                      <Button variant="link" onClick={() => navigate('/jobs/post')}>
                        Post your first job
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Candidates */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent Candidates</CardTitle>
                  <CardDescription>Latest applications to review</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('candidates')}>
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {applications.slice(0, 5).map((app) => (
                    <CandidateCard
                      key={app.id}
                      application={app}
                      onUpdateStatus={handleUpdateApplicationStatus}
                      onView={() => {}}
                    />
                  ))}
                  {applications.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No applications yet</p>
                      <p className="text-sm">Applications will appear here when candidates apply</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Job Postings Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>All Job Postings</CardTitle>
                  <CardDescription>Manage your job listings</CardDescription>
                </div>
                <Button onClick={() => navigate('/jobs/post')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Post New Job
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {jobs.map((job) => (
                    <JobPostingCard
                      key={job.id}
                      job={job}
                      onView={() => navigate(`/jobs/${job.id}`)}
                      onManage={() => navigate(`/jobs/${job.id}/manage`)}
                    />
                  ))}
                  {jobs.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No job postings yet</p>
                      <Button onClick={() => navigate('/jobs/post')}>
                        Post your first job
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Candidates Tab */}
          <TabsContent value="candidates">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>All Candidates</CardTitle>
                    <CardDescription>Review and manage all applications</CardDescription>
                  </div>
                  {/* Badge Filters */}
                  <div className="flex flex-wrap gap-2" data-testid="badge-filters">
                    <Button 
                      size="sm" 
                      variant={badgeFilter === null ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter(null)}
                    >
                      All ({applications.length})
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badgeFilter === 'any' ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter('any')}
                      className="gap-1"
                    >
                      🏅 With Badges
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badgeFilter === 'profile-optimised' ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter('profile-optimised')}
                      className="gap-1"
                    >
                      🟢 Profile Optimised
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badgeFilter === 'portfolio-reviewed' ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter('portfolio-reviewed')}
                      className="gap-1"
                    >
                      🟢 Portfolio Reviewed
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badgeFilter === 'pricing-ready' ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter('pricing-ready')}
                      className="gap-1"
                    >
                      🟢 Pricing Ready
                    </Button>
                    <Button 
                      size="sm" 
                      variant={badgeFilter === 'client-ready' ? 'default' : 'outline'}
                      onClick={() => setBadgeFilter('client-ready')}
                      className="gap-1"
                    >
                      🟢 Client-Ready
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getFilteredApplications().map((app) => (
                    <CandidateCard
                      key={app.id}
                      application={app}
                      onUpdateStatus={handleUpdateApplicationStatus}
                      onView={() => {}}
                    />
                  ))}
                  {getFilteredApplications().length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>{badgeFilter ? 'No candidates match this badge filter' : 'No applications to review'}</p>
                      {badgeFilter && (
                        <Button variant="link" onClick={() => setBadgeFilter(null)}>
                          Clear filter
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Screening Services Tab */}
          <TabsContent value="screening">
            <Card>
              <CardHeader>
                <CardTitle>Candidate Screening Services</CardTitle>
                <CardDescription>Professional screening to find the best candidates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <UserCheck className="h-8 w-8 text-blue-600 mb-3" />
                      <h4 className="font-semibold mb-1">Technical Screening</h4>
                      <p className="text-sm text-gray-500 mb-3">Verify technical skills and experience</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=employer-services')}>
                        Get Started
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <Search className="h-8 w-8 text-purple-600 mb-3" />
                      <h4 className="font-semibold mb-1">Background Check</h4>
                      <p className="text-sm text-gray-500 mb-3">Comprehensive background verification</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=employer-services')}>
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-4">
                      <Target className="h-8 w-8 text-emerald-600 mb-3" />
                      <h4 className="font-semibold mb-1">Talent Matching</h4>
                      <p className="text-sm text-gray-500 mb-3">AI-powered candidate matching</p>
                      <Button size="sm" className="w-full" onClick={() => navigate('/jobs?tab=employer-services')}>
                        Try Now
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmployerDashboard;
